import React from "react";
import { Box, Paper, Typography, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, Button, Link } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import api from "../Auth/Api";
import globals from "../Globals";
import { replaceDeep } from "../Layout/Router";
import { PageContainer, SectionHeader, DataTable, SelectorBar, SelectField, StatCard, formatDate, relTime } from "../Layout/Common";

// System Health → Latency: player request time per title/branch/module/action, as percentiles (backend `health latency`
// over HealthSample.actions). Each request's time splits into read (the request body arriving from the client), handler
// (the module's work) and other (login data, nonce, crypto). A row selects the chart's series.
// Deep link: /health/latency/<title>/<branch>/<module>/<action>.

const REFRESH_MS = 60000;
const RANGES = [{ value: 1, label: "1h" }, { value: 6, label: "6h" }, { value: 24, label: "24h" }, { value: 72, label: "3d" }, { value: 168, label: "7d" }];
const FIXED = { "& table": { tableLayout: "fixed" }, "& .MuiTableCell-root": { px: 1, fontSize: 13 } };
const ELLIPSIS = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const num = (v) => Number(v ?? 0).toLocaleString();
const msv = (v) => v == null ? "—" : v < 10 ? v.toFixed(1) : Math.round(v).toLocaleString();
const ms = (v) => v == null ? "—" : `${msv(v)} ms`;
const maxv = (v) => v == null ? "—" : v >= 60000 ? "> 30 s" : msv(v);
const bytes = (v) => v == null ? "—" : v < 1024 ? `${v} B` : `${(v / 1024).toFixed(1)} KB`;
const pair = (p) => p ? `${msv(p.p50)} · ${msv(p.p95)}` : "—";
const titleName = (id) => {
    const t = globals.loginData?.titles?.titles?.[id];
    return t?.title ?? t?.name ?? id;
};

export default class HealthLatencyPage extends React.Component {
    constructor(props) {
        super(props);
        const deep = globals.route?.deep ?? [];
        this.state = { hours: 24, titleId: "", branch: "", select: deep.length === 4 ? deep.join("|") : "", data: null, loadedAt: null };
    }

    componentDidMount() {
        this.load();
        this.timer = setInterval(() => this.load(), REFRESH_MS);
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    load = () => {
        const { hours, titleId, branch, select } = this.state;
        const payload = { hours };
        if (titleId) payload.titleId = titleId;
        if (branch) payload.branch = branch;
        if (select) payload.select = select;
        return api.health("Loading latency", ["latency"], { payload, silent: true })
            .then(r => { if (r && r.actions) this.setState({ data: r, loadedAt: Date.now() }); })
            .catch(() => {});
    }

    setRange = (e, hours) => {
        if (hours == null) return;
        this.setState({ hours }, this.load);
    }

    setFilter = (key, value) => this.setState({ [key]: value }, this.load);

    select = (id) => {
        const select = id === "all" ? "" : id;
        this.setState({ select }, this.load);
        replaceDeep(select ? select.split("|") : []);
    }

    timeLabel = (t) => {
        const d = new Date(t);
        const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        return this.state.hours > 24 ? `${d.getMonth() + 1}/${d.getDate()} ${hm}` : hm;
    }

    selectedRow() {
        const { data, select } = this.state;
        return (select && data.actions.find(a => a.id === select)) || data.all;
    }

    rowLabel = (row) => row.id === "all" ? "All requests" : `${titleName(row.title)} · ${row.branch} · ${row.module} · ${row.action}`;

    renderFilters() {
        const { data, titleId, branch } = this.state;
        const facets = data?.facets ?? { titles: [], branches: [] };
        return (
            <SelectorBar sx={{ flexWrap: "wrap" }}>
                <SelectField size="small" label="Game" minWidth={160} value={titleId} onChange={v => this.setFilter("titleId", v)}
                             options={[{ value: "", label: "All" }, ...facets.titles.map(t => ({ value: t, label: titleName(t) }))]} />
                <SelectField size="small" label="Branch" minWidth={160} value={branch} onChange={v => this.setFilter("branch", v)}
                             options={[{ value: "", label: "All" }, ...facets.branches.map(b => ({ value: b, label: b }))]} />
                <Button size="small" onClick={() => this.setState({ titleId: "", branch: "" }, this.load)} disabled={!titleId && !branch}>Clear</Button>
            </SelectorBar>
        );
    }

    renderCards() {
        const row = this.selectedRow();
        const failed = row.err + row.rejected;
        return (
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <StatCard label="Requests" value={num(row.n)} secondary={`${num(failed)} failed · ${num(row.cached)} from cache · ${num(row.twin ?? 0)} twins`}
                          tertiary={`avg ${ms(row.avgMs)}`} />
                <StatCard label="Total p50" value={ms(row.total?.p50)} secondary={`p90 ${ms(row.total?.p90)}`} />
                <StatCard label="Total p95" value={ms(row.total?.p95)} secondary={`p99 ${ms(row.total?.p99)}`} tertiary={`max ≤ ${maxv(row.total?.max)} ms`} />
                <StatCard label="Read p95 (body in)" value={ms(row.read?.p95)} secondary={`p50 ${ms(row.read?.p50)}`} tertiary={`avg ${ms(row.avgReadMs)}`} />
                <StatCard label="Handler p95" value={ms(row.handler?.p95)} secondary={`p50 ${ms(row.handler?.p50)}`} tertiary={`avg ${ms(row.avgHandlerMs)}`} />
                <StatCard label="Other p95 (login, nonce)" value={ms(row.other?.p95)} secondary={`p50 ${ms(row.other?.p50)}`} />
            </Box>
        );
    }

    renderChart() {
        const { data } = this.state;
        const s = data.series;
        const row = this.selectedRow();
        return (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Latency over time: {this.rowLabel(row)} ({data.binMin} min bins, ms)
                </Typography>
                {s.length === 0
                    ? <Typography variant="body2" color="text.secondary">No timed requests in this range.</Typography>
                    : <ChartContainer height={280}
                                      xAxis={[{ id: "t", scaleType: "band", data: s.map(r => this.timeLabel(r.t)), tickLabelStyle: { fontSize: 10 } }]}
                                      yAxis={[{ id: "ms" }]}
                                      series={[
                                          { type: "line", id: "p50", yAxisId: "ms", data: s.map(r => r.p50), label: "p50", color: "#2e7d32" },
                                          { type: "line", id: "p95", yAxisId: "ms", data: s.map(r => r.p95), label: "p95", color: "#1976d2" },
                                          { type: "line", id: "p99", yAxisId: "ms", data: s.map(r => r.p99), label: "p99", color: "#d32f2f" },
                                          { type: "line", id: "read", yAxisId: "ms", data: s.map(r => r.readP95), label: "read p95", color: "#ed6c02" },
                                          { type: "line", id: "handler", yAxisId: "ms", data: s.map(r => r.handlerP95), label: "handler p95", color: "#6a1b9a", connectNulls: true },
                                      ]}>
                        <LinePlot />
                        <MarkPlot />
                        <ChartsXAxis axisId="t" />
                        <ChartsYAxis axisId="ms" />
                        <ChartsTooltip />
                        <ChartsLegend />
                    </ChartContainer>}
            </Paper>
        );
    }

    renderTable() {
        const { data } = this.state;
        const selectedId = this.selectedRow().id;
        const rows = [data.all, ...data.actions];
        const right = { align: "right" };
        const columns = [
            { id: "title", label: "Game · branch", width: "10%", sx: ELLIPSIS, render: r => r.id === "all" ? "—" : (
                <>
                    <Box sx={ELLIPSIS}>{titleName(r.title)}</Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", ...ELLIPSIS }}>{r.branch}</Typography>
                </>
            ) },
            { id: "action", label: "Module · action", width: "12%", sx: ELLIPSIS, render: r => (
                <Link component="button" underline="hover" onClick={() => this.select(r.id)}
                      sx={{ fontWeight: r.id === selectedId ? 700 : 400, textAlign: "left", ...ELLIPSIS, maxWidth: "100%" }}>
                    {r.id === "all" ? "All requests" : `${r.module} · ${r.action}`}
                </Link>
            ) },
            { id: "n", label: "Requests", width: "6%", ...right, render: r => num(r.n) },
            { id: "failed", label: "Failed", width: "5%", ...right, render: r => (
                <Tooltip title={`${num(r.err)} errors · ${num(r.rejected)} rejected`}><span>{num(r.err + r.rejected)}</span></Tooltip>
            ) },
            { id: "cached", label: "From cache", width: "5%", ...right, render: r => num(r.cached) },
            { id: "twin", label: <Tooltip title="Attempts refused 'double tap' while another attempt of the same request (one nonce) ran: each one a write that ran twice before the nonce claim (package requestpath)"><span>Twins</span></Tooltip>,
              width: "4%", ...right, render: r => num(r.twin ?? 0) },
            { id: "size", label: "Size in · out", width: "7%", ...right, render: r => `${bytes(r.bytesIn)} · ${bytes(r.bytesOut)}` },
            { id: "p50", label: "p50", width: "5%", ...right, render: r => msv(r.total?.p50) },
            { id: "p90", label: "p90", width: "5%", ...right, render: r => msv(r.total?.p90) },
            { id: "p95", label: "p95", width: "5%", ...right, render: r => <strong>{msv(r.total?.p95)}</strong> },
            { id: "p99", label: "p99", width: "5%", ...right, render: r => msv(r.total?.p99) },
            { id: "max", label: <Tooltip title="Upper bound of the slowest histogram bucket"><span>Max ≤</span></Tooltip>, width: "6%", ...right, render: r => maxv(r.total?.max) },
            { id: "read", label: "Read p50 · p95", width: "8%", ...right, render: r => pair(r.read) },
            { id: "handler", label: "Handler p50 · p95", width: "9%", ...right, render: r => pair(r.handler) },
            { id: "other", label: "Other p50 · p95", width: "8%", ...right, render: r => pair(r.other) },
        ];
        return data.actions.length === 0
            ? <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>No per-action timing in this range yet.</Typography>
            : <DataTable columns={columns} rows={rows} getRowKey={r => r.id} sx={{ ...FIXED, mb: 2 }} />;
    }

    render() {
        const { data, hours, loadedAt } = this.state;
        return (
            <PageContainer maxWidth={1600}>
                <SectionHeader title="Request Latency">
                    {loadedAt && <Typography variant="caption" color="text.secondary">updated {relTime(loadedAt)}</Typography>}
                    <ToggleButtonGroup size="small" exclusive value={hours} onChange={this.setRange}>
                        {RANGES.map(r => <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                {this.renderFilters()}
                {!data
                    ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    : <>
                        {this.renderCards()}
                        {this.renderChart()}
                        {this.renderTable()}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                            Times are in ms, measured inside the function from the moment it starts running until the response is ready: the player's network
                            round trip, TLS and the Azure front end are not included. Read = waiting for the request body (it streams in from the device, so a
                            slow upload shows here); Handler = the module's own work (retries answered from the nonce cache skip it); Other = login data, nonce
                            lookup/save and crypto. Percentiles come from per-action histograms (buckets about 25% wide, interpolated) merged across
                            instances; Max is the upper bound of the slowest bucket.
                            {data.since ? ` Per-action timing in this range since ${formatDate(data.since)}.` : ""}
                        </Typography>
                    </>}
            </PageContainer>
        );
    }
}
